import { useErrorHandler } from '@/hooks/use-error-handler';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cloneTemplate,
  createTemplate,
  createTemplateField,
  createTemplateRequest,
  createTemplateScreen,
  deleteTemplate,
  deleteTemplateField,
  deleteTemplateScreen,
  generatePreviewToken,
  getFieldsByScreenId,
  getScreensByTemplateId,
  getTemplateBasicById,
  getTemplateByIdWithScreensAndFields,
  getTemplateBySlugWithScreensAndFields,
  getTemplateFields,
  getTemplates,
  getTemplateScreens,
  getAvailableTemplatesForOrg,
  getAvailableTemplatesForOrgPaginated,
  getOwnTemplatesForOrg,
  getOwnTemplatesForOrgPaginated,
  revokePreviewToken,
  updateTemplate,
  updateTemplateField,
  updateTemplateScreen,
  getTemplateBySlugWithVariablesAndDocx,
} from '../services/templates.service';
import type {
  AvailableTemplatesParams,
  CloneTemplateArgs,
  OwnTemplatesParams,
} from '../services/templates.service';
import type {
  PaginationParams,
  Template,
  TemplateFieldInsertInput,
  TemplateFieldUpdateInput,
  TemplateInsertInput,
  TemplateRequestInsertInput,
  TemplateScreenInsertInput,
  TemplateScreenUpdateInput,
  TemplateUpdateInput,
  TemplateWithScreens,
  TemplateWithVariables,
} from '../types/templates.types';

// ============================================================
// Composite hook — replaces old Prisma `findUnique({ include })`
// ============================================================

/**
 * Fetches a template by slug together with its screens and fields,
 * merging them into a single `TemplateWithScreens` object that
 * mirrors the old Next.js Prisma shape.
 */
export const useGetTemplateBySlug = (slug: string | undefined) => {
  return useGlobalQuery<TemplateWithScreens | null>({
    queryKey: ['template', slug],
    queryFn: () => {
      if (!slug) return Promise.resolve(null);
      return getTemplateBySlugWithScreensAndFields(slug);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetDocxTemplateBySlug = (slug: string | undefined) => {
  return useGlobalQuery<TemplateWithVariables | null>({
    queryKey: ['docx-template', slug],
    queryFn: () => {
      if (!slug) return Promise.resolve(null);
      return getTemplateBySlugWithVariablesAndDocx(slug);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// ============================================================
// Composite hook — public + org templates (replaces old GET /api/templates?orgId=xxx)
// ============================================================

/**
 * Fetches public templates (no org) merged with org-specific available templates.
 * Matches the old Next.js API: GET /api/templates?orgId=xxx
 */
export const useGetAvailableTemplatesForOrg = (orgId?: string | null) => {
  return useGlobalQuery<Template[]>({
    queryKey: ['availableTemplatesForOrg', orgId],
    queryFn: () => getAvailableTemplatesForOrg(orgId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Paginated available templates for org — server-side search, category filter, pagination.
 */
export const useGetAvailableTemplatesForOrgPaginated = (params: AvailableTemplatesParams) => {
  return useGlobalQuery({
    queryKey: ['availableTemplatesForOrgPaginated', params],
    queryFn: () => getAvailableTemplatesForOrgPaginated(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Paginated own templates for org — server-side search + pagination.
 * Includes drafts (does not enforce Available: true).
 */
export const useGetOwnTemplatesForOrgPaginated = (params: OwnTemplatesParams) => {
  return useGlobalQuery({
    queryKey: ['ownTemplatesForOrgPaginated', params],
    queryFn: () => getOwnTemplatesForOrgPaginated(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches org-specific templates (no public templates).
 * Matches the old Next.js API: GET /api/templates?orgId=xxx
 */
export const useGetOwnTemplatesForOrg = (orgId?: string | null) => {
  return useGlobalQuery<Template[]>({
    queryKey: ['ownTemplatesForOrg', orgId],
    queryFn: () => getOwnTemplatesForOrg(orgId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// ============================================================
// Template hooks
// ============================================================

export const useGetTemplates = (params: PaginationParams) => {
  return useGlobalQuery({
    queryKey: ['templates', params],
    queryFn: () => getTemplates(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (input: TemplateInsertInput) => createTemplate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Template created successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to create template' });
    },
  });
};

/**
 * Deep-clones a template into a new draft owned by `orgId`. Unlike
 * `useCreateTemplate`, this also invalidates `ownTemplatesForOrgPaginated` —
 * the query that backs the org templates list — so the copy appears immediately.
 */
export const useCloneTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (args: CloneTemplateArgs) => cloneTemplate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrgPaginated'] });
      queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrg'] });
      queryClient.invalidateQueries({ queryKey: ['availableTemplatesForOrg'] });
      queryClient.invalidateQueries({ queryKey: ['availableTemplatesForOrgPaginated'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast({ variant: 'success', title: t('ORGTEMPLATES_CLONE_TOAST_SUCCESS') });
    },
    onError: (error: Error) => {
      handleError(error, { title: t('ORGTEMPLATES_CLONE_TOAST_ERROR') });
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (variables: { itemId: string; input: TemplateUpdateInput }) =>
      updateTemplate(variables.itemId, variables.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrg'] });
      queryClient.invalidateQueries({ queryKey: ['availableTemplatesForOrg'] });
      queryClient.invalidateQueries({ queryKey: ['availableTemplatesForOrgPaginated'] });
      toast({ variant: 'success', title: 'Template updated successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to update template' });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (itemId: string) => deleteTemplate(itemId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrg'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['availableTemplatesForOrg'], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['ownTemplatesForOrgPaginated'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['availableTemplatesForOrgPaginated'],
        exact: false,
      });
      toast({ variant: 'success', title: 'Template deleted successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to delete template' });
    },
  });
};

// ============================================================
// TemplateScreen hooks
// ============================================================

export const useGetTemplateScreens = (params: PaginationParams) => {
  return useGlobalQuery({
    queryKey: ['templateScreens', params],
    queryFn: () => getTemplateScreens(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetScreensByTemplateId = (templateId: string) => {
  return useGlobalQuery({
    queryKey: ['templateScreens', { templateId }],
    queryFn: () => getScreensByTemplateId(templateId),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTemplateScreen = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (input: TemplateScreenInsertInput) => createTemplateScreen(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateScreens'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Screen created successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to create screen' });
    },
  });
};

export const useUpdateTemplateScreen = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (variables: { itemId: string; input: TemplateScreenUpdateInput }) =>
      updateTemplateScreen(variables.itemId, variables.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateScreens'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Screen updated successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to update screen' });
    },
  });
};

export const useDeleteTemplateScreen = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (itemId: string) => deleteTemplateScreen(itemId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateScreens'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Screen deleted successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to delete screen' });
    },
  });
};

// ============================================================
// TemplateField hooks
// ============================================================

export const useGetTemplateFields = (params: PaginationParams) => {
  return useGlobalQuery({
    queryKey: ['templateFields', params],
    queryFn: () => getTemplateFields(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetFieldsByScreenId = (screenId: string) => {
  return useGlobalQuery({
    queryKey: ['templateFields', { screenId }],
    queryFn: () => getFieldsByScreenId(screenId),
    enabled: !!screenId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTemplateField = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (input: TemplateFieldInsertInput) => createTemplateField(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateFields'] });
      queryClient.invalidateQueries({ queryKey: ['templateScreens'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Field created successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to create field' });
    },
  });
};

export const useUpdateTemplateField = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (variables: { itemId: string; input: TemplateFieldUpdateInput }) =>
      updateTemplateField(variables.itemId, variables.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateFields'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Field updated successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to update field' });
    },
  });
};

export const useDeleteTemplateField = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (itemId: string) => deleteTemplateField(itemId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateFields'] });
      queryClient.invalidateQueries({ queryKey: ['templateScreens'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
      toast({ variant: 'success', title: 'Field deleted successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to delete field' });
    },
  });
};

// ============================================================
// Get template by ID (with screens & fields)
// ============================================================

export const useGetTemplateById = (templateId: string | undefined) => {
  return useGlobalQuery<TemplateWithScreens | null>({
    queryKey: ['template', templateId],
    queryFn: () => {
      if (!templateId) return Promise.resolve(null);
      return getTemplateByIdWithScreensAndFields(templateId);
    },
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * The template row on its own — for callers that need title/slug/kind/availability and never
 * touch screens or fields. One request instead of two-plus serial ones.
 *
 * Distinct query key from `useGetTemplateById` on purpose: same key with a different fetcher
 * would let two shapes race into one cache entry.
 */
export const useGetTemplateBasicById = (templateId: string | undefined) => {
  return useGlobalQuery<Template | null>({
    queryKey: ['templateBasic', templateId],
    queryFn: () => {
      if (!templateId) return Promise.resolve(null);
      return getTemplateBasicById(templateId);
    },
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// ============================================================
// Preview token
// ============================================================

export const useGeneratePreviewToken = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (templateId: string) => generatePreviewToken(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to generate preview token' });
    },
  });
};

export const useRevokePreviewToken = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (templateId: string) => revokePreviewToken(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to revoke preview token' });
    },
  });
};

// ============================================================
// Template request
// ============================================================

export const useCreateTemplateRequest = () => {
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (input: TemplateRequestInsertInput) => createTemplateRequest(input),
    onSuccess: () => {
      toast({ variant: 'success', title: 'Template request submitted successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to submit template request' });
    },
  });
};
